import { useStoreon } from "storeon/react";
import { useState, useEffect } from "react";

export const useCanvasMetrics = (k = 0.1) => {
  const { canvas } = useStoreon("canvas");
  const [metrics, setMetics] = useState({
    width: canvas?.cutW * k,
    height: canvas?.cutH * k,
    radius: (canvas?.cutRadius ?? 0) * k,
  });

  useEffect(() => {
    if (!canvas) {
      return;
    }

    const updateMetrics = () =>
      setMetics({
        width: parseFloat((canvas?.cutW * k).toFixed(2)),
        height: parseFloat((canvas?.cutH * k).toFixed(2)),
        radius: parseFloat((canvas?.cutRadius * k).toFixed(2)) ?? 0,
      });
    updateMetrics();
    //FIXME: unsubscribe on 'k' changes
    canvas.on("cutzone:updated", updateMetrics);
    canvas.on("canvas:loaded", updateMetrics);
    return () => {
      canvas.off("cutzone:updated", updateMetrics);
      canvas.off("canvas:loaded", updateMetrics);
    };
  }, [canvas, k]);

  return metrics;
};

export const defaultSettings = {
  minWidth: 10,
  minHeight: 10,
  maxWidth: 300,
  maxHeight: 300,
  minDPI: 100,
  isDynamicSize: true,
  isDynamicRadius: true,
  maxRadius: 100,
  minRadius: 1,
  cutEnabled: true,
  cutSize: 3,
};

export const useCanvasSettings = () => {
  const { canvas } = useStoreon("canvas");

  const [settings, setSettings] = useState(defaultSettings);

  useEffect(() => {
    if (!canvas) {
      return;
    }
    const updateSettings = () =>
      setSettings({ ...defaultSettings, ...canvas.settings });
    updateSettings();
    canvas.on("canvas:loaded", updateSettings);
    return () => canvas.off("canvas:loaded", updateSettings);
  }, [canvas]);

  return settings;
};

export const getCanvasZoom = (canvas) => {
  const { width, height, cutW, cutH, settings } = canvas;
  const { cutSize = 0 } = settings;
  const w = cutW + cutSize * 2;
  const h = cutH + cutSize * 2;
  const wk = width / w;
  const hk = height / h;
  const fitZoomValue = wk > hk ? hk : wk;
  const k = 0.92;
  const min = fitZoomValue * 0.4 * k; //40%
  const max = fitZoomValue * 3 * k; // 300%
  const full = fitZoomValue * k; //100%
  return {
    current: canvas.getZoom(),
    min,
    max,
    full,
    step: (max - min) / 100,
  };
};

export const useCanvasZoom = () => {
  const { canvas } = useStoreon("canvas");
  const [zoom, setZoom] = useState({
    current: 0.1,
    min: 0.1,
    max: 2,
    full: 1,
    step: 0.05,
  });

  useEffect(() => {
    if (!canvas) {
      return;
    }
    const updateZoomMetrics = () => {
      setZoom(getCanvasZoom(canvas));
    };
    canvas.on("canvas:zoom", updateZoomMetrics);
    canvas.on("canvas:viewport", updateZoomMetrics);
    canvas.on("canvas:loaded", updateZoomMetrics);
    return () => {
      canvas.off("canvas:zoom", updateZoomMetrics);
      canvas.off("canvas:viewport", updateZoomMetrics);
      canvas.off("canvas:loaded", updateZoomMetrics);
    };
  }, [canvas]);
  return zoom;
};
